/**
 * Rules that judge a composition against the video's design system rather than against the canvas.
 * They are the enforcement half of `write_design_system`: a graphic that sizes type or draws
 * strokes with its own numbers has opted out of the one thing holding seven separately authored
 * graphics together. They judge what rendered rather than what was typed, so a composition
 * conforms whether it reaches the tokens through `var()` or writes their values out. All of them
 * report nothing for a video with no design system.
 */
import { declaresSeries, isCompositionFrame } from './dataRules';
import { parseDesignTokens, plotBoxOf, tokenPx, typeScalePx } from './designSystemTokens';
import type { PlotBox } from './designSystemTokens';
import { describeElement } from './measure';
import type { ElementMeasurement, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

const OPACITY_PRESENT_THRESHOLD = 0.05;
/** Rounding between an authored `var()` and its computed px, not a different size. */
const TYPE_MATCH_TOL_PX = 1;
/** How far outside the declared box a chart may sit before the frame reads as two chart boxes. */
const PLOT_BOX_TOL_PX = 8;
/** A series is either standing on the baseline or it isn't; this is anti-aliasing, not slack. */
const BASELINE_TOL_PX = 4;
const ALLOW_LITERAL_ATTR = '[data-l5-allow-literal]';

/** Every visible element once, at the first frame it was measured in. */
function visibleOnce(context: RuntimeLintContext): ElementMeasurement[] {
    const seen = new Set<Element>();
    const measurements: ElementMeasurement[] = [];
    for (const frame of context.frames ?? []) {
        for (const measurement of frame.elements) {
            if (measurement.opacity < OPACITY_PRESENT_THRESHOLD) continue;
            if (seen.has(measurement.element)) continue;
            seen.add(measurement.element);
            measurements.push(measurement);
        }
    }
    return measurements;
}

/**
 * The stroke weight this element is drawn with, or null when it draws no stroke. Whether it draws
 * one at all is decided by the resolved paint, not by the presence of a `stroke` attribute:
 * `stroke-width` applies to every element and resolves to its initial `1`, so an unstroked `<div>`
 * reports a 1px stroke and would be judged against the token. Width itself reads the authored
 * attribute ahead of the computed value, because what the author typed is what a finding names.
 */
function strokeWidthOf(element: Element): number | null {
    const view = element.ownerDocument?.defaultView;
    const paint = element.getAttribute('stroke') || view?.getComputedStyle(element).stroke;
    if (!paint || paint === 'none') return null;
    const raw =
        (element as SVGElement).style?.strokeWidth ||
        element.getAttribute('stroke-width') ||
        view?.getComputedStyle(element).strokeWidth ||
        '';
    const width = parseFloat(raw);
    return Number.isFinite(width) && width > 0 ? width : null;
}

function nearest(value: number, candidates: number[]): number {
    return candidates.reduce((best, candidate) =>
        Math.abs(candidate - value) < Math.abs(best - value) ? candidate : best,
    );
}

/** Which type token carries `size`, for naming the one the author should have reached for. */
function typeTokenName(size: number, tokens: ReturnType<typeof parseDesignTokens>): string {
    const named = [...tokens].find(
        ([name, value]) => name.startsWith('--ds-type-') && parseFloat(value) === size,
    );
    return named ? named[0] : '--ds-type-*';
}

export const designTokenBypass: RuntimeLintRule = {
    id: 'design-token-bypass',
    severity: 'warning',
    guidance:
        "Size every piece of text with the design system's type scale — " +
        'var(--ds-type-hero), var(--ds-type-headline) or var(--ds-type-label) — and draw every ' +
        'stroked object at var(--ds-stroke). A literal px size or stroke width is the exception: ' +
        'mark it data-l5-allow-literal when the design genuinely needs one.',
    check(context: RuntimeLintContext): string[] {
        if (!context.designSystemCss) return [];
        const tokens = parseDesignTokens(context.designSystemCss);
        const scale = typeScalePx(tokens);
        const stroke = tokenPx(tokens, '--ds-stroke');
        const messages: string[] = [];

        for (const measurement of visibleOnce(context)) {
            const { element } = measurement;
            if (element.closest(ALLOW_LITERAL_ATTR)) continue;

            if (measurement.hasText && measurement.fontSizePx > 0 && scale.length > 0) {
                const size = measurement.fontSizePx;
                const closest = nearest(size, scale);
                if (Math.abs(closest - size) > TYPE_MATCH_TOL_PX) {
                    messages.push(
                        `${describeElement(element)} renders text at ${Math.round(size)}px, which ` +
                            `is not a size in this video's type scale (${scale.join('/')}px). Use ` +
                            `var(${typeTokenName(closest, tokens)}) — ${closest}px — or the token ` +
                            `the copy's role calls for, so every graphic types alike.`,
                    );
                }
            }

            if (stroke !== null) {
                const width = strokeWidthOf(element);
                if (width !== null && Math.abs(width - stroke) > TYPE_MATCH_TOL_PX) {
                    messages.push(
                        `${describeElement(element)} is drawn with stroke-width ${width}, but this ` +
                            `video draws at one weight: var(--ds-stroke) (${stroke}px). Use the ` +
                            `token, or mark it data-l5-allow-literal if this object is meant to be ` +
                            `heavier.`,
                    );
                }
            }
        }
        return messages;
    },
};

function outsideBox(rect: Rect, box: PlotBox): boolean {
    return (
        rect.x < box.x - PLOT_BOX_TOL_PX ||
        rect.y < box.y - PLOT_BOX_TOL_PX ||
        rect.x + rect.width > box.x + box.width + PLOT_BOX_TOL_PX ||
        rect.y + rect.height > box.y + box.height + PLOT_BOX_TOL_PX
    );
}

/**
 * Bars and trend lines measure up from a baseline; a ring, a pie or a unit field has no foot, and
 * holding one to the baseline turns a source document's donut into a bar set.
 */
function drawsFromBaseline(element: Element): boolean {
    const marks = '[data-l5-bar], [data-l5-series]';
    return element.matches(marks) || element.querySelector(marks) !== null;
}

export const chartOutsidePlotBox: RuntimeLintRule = {
    id: 'chart-outside-plot-box',
    severity: 'warning',
    guidance:
        "Draw every chart, bar set and plotted series in the design system's plot box — " +
        'var(--ds-plot-x)/y/w/h — with bars and trend lines standing on var(--ds-baseline-y). ' +
        'Two charts in one video drawn in different boxes read as two videos.',
    check(context: RuntimeLintContext): string[] {
        if (!context.designSystemCss) return [];
        const box = plotBoxOf(parseDesignTokens(context.designSystemCss));
        if (!box) return [];

        const messages: string[] = [];
        const elements = visibleOnce(context);
        const charts = elements.filter(
            ({ element, rect }) =>
                !isCompositionFrame(rect, context.canvasSize) && declaresSeries(element),
        );
        for (const { element, rect } of charts) {
            // One graphic, one finding: the declared container and the <svg> or <g> holding its
            // shapes both draw the same series, and naming both reads as two charts out of place.
            if (charts.some(({ element: other }) => other !== element && other.contains(element))) {
                continue;
            }
            const standsOnBaseline = drawsFromBaseline(element);
            const foot = rect.y + rect.height;
            const offBaseline =
                standsOnBaseline && Math.abs(foot - box.baselineY) > BASELINE_TOL_PX;
            if (!outsideBox(rect, box) && !offBaseline) continue;
            const where = standsOnBaseline
                ? `--ds-plot-h ${box.height}, baseline ${box.baselineY}). Position it from those ` +
                  `tokens and anchor the series to var(--ds-baseline-y)`
                : `--ds-plot-h ${box.height}). Position it from those tokens`;
            messages.push(
                `${describeElement(element)} plots at ${Math.round(rect.x)},${Math.round(rect.y)} ` +
                    `${Math.round(rect.width)}×${Math.round(rect.height)}, outside this video's ` +
                    `plot box (--ds-plot-x ${box.x}, --ds-plot-y ${box.y}, --ds-plot-w ` +
                    `${box.width}, ${where}, so every data graphic in the video sits in the same box.`,
            );
        }
        return messages;
    },
};

export const CORE_DESIGN_SYSTEM_LINT_RULES: RuntimeLintRule[] = [
    designTokenBypass,
    chartOutsidePlotBox,
];
